/**
 * Thrown when a query completes without the record a page needs, such as an
 * unknown uuid. A `PlaceholderErrorBoundary` given a `notFound` prop renders it
 * as a not-found state with a link back, instead of the retry modal: retrying
 * cannot bring the record back.
 */
class NotFoundError extends Error {
  /**
   * Creates a not-found error for a missing record.
   * @param {string} message - Diagnostic message for logs.
   */
  constructor(message) {
    super(message);
    this.name = 'NotFoundError';
  }
}

/**
 * Reports whether GraphQL errors all mean that a root record is missing: each
 * carries the API's `NOT_FOUND` code on the root field itself. A `NOT_FOUND`
 * on a nested field, such as a member's property, can null its non-nullable
 * parent, but the record itself exists, so it does not count.
 * @param {Array<object>|null|undefined} errors - GraphQL errors to classify.
 * @param {number} rootPathLength - Length of a root field error's `path`: 1 for
 *   response errors, 0 for errors caught by `@catch` on that field, whose paths
 *   are relative to it.
 * @returns {boolean} - True when there is at least one error and all are root `NOT_FOUND` errors.
 */
export function areRootNotFoundErrors(errors, rootPathLength) {
  return Array.isArray(errors) && errors.length > 0
    && errors.every((graphqlError) => graphqlError?.extensions?.code === 'NOT_FOUND'
      && (graphqlError.path?.length ?? 0) === rootPathLength);
}

/**
 * Reports whether an error means the requested record does not exist: either a
 * `NotFoundError`, or a Relay network error whose GraphQL errors all carry the
 * API's `NOT_FOUND` code on a root field, as an unknown uuid returns.
 * @param {Error} error - Error caught by the boundary.
 * @returns {boolean} - True when the record is missing rather than unavailable.
 */
export function isNotFoundError(error) {
  return error instanceof NotFoundError || areRootNotFoundErrors(error?.source?.errors, 1);
}

export default NotFoundError;
